import { z } from "zod";
import { request } from "./api-client";

/** C2 request body. `next` is validated by the api and the Worker; admin only forwards it. */
export type BlogCmsTicketRequest = { mode?: "human"; next?: string } | { mode: "agent" };

/** C2 200 `data`. `expiresIn` is 45 today. */
export interface BlogCmsTicketResponse {
  ticket: string;
  url: string;
  expiresIn: number;
}

/**
 * The wire shape. C2 answers `{ data: … }` like every admin route, and `request<T>` does not
 * unwrap it (it is a bare `response.json() as T`), so an envelope drift would otherwise surface as
 * an `undefined` src on the iframe. `trial-policy.ts` records the shipped crash that motivates
 * parsing here instead of asserting.
 */
const ticketEnvelopeSchema = z.object({
  data: z.object({
    ticket: z.string().min(1),
    url: z.url(),
    expiresIn: z.number().int().positive(),
  }),
});

/**
 * Mint a short-lived blog-cms ticket (C2). The ticket is never logged or stored: callers keep the
 * returned `url` in memory only.
 */
export async function requestBlogCmsTicket(
  body?: BlogCmsTicketRequest,
  signal?: AbortSignal,
): Promise<BlogCmsTicketResponse> {
  const raw = await request<unknown>("/blog-cms/ticket", {
    method: "POST",
    body: JSON.stringify(body ?? {}),
    cache: "no-store",
    signal,
  });
  const parsed = ticketEnvelopeSchema.safeParse(raw);
  if (!parsed.success) {
    throw new Error("blog_cms_bad_response");
  }
  return parsed.data.data;
}
